"use client";

import { ArrowUp, ChevronDown, Plus } from "lucide-react";
import { useRef } from "react";

import { ModeMenu } from "./ModeMenu";
import { COMPOSER_TOOLBAR_HEIGHT } from "./composerSizing";
import { Button } from "@/shared/ui/shadcn-components/button";
import {
  DEFAULT_COMPOSER_MODE,
  getComposerMode,
  type ComposerMode,
  type ComposerModeId,
} from "./modes";

interface ComposerToolbarProps {
  canSend: boolean;
  selectedModeId?: ComposerModeId;
  onModeChange?: (mode: ComposerMode) => void;
  onAttachFiles?: (files: FileList) => void;
  onSend: () => void;
}

export function ComposerToolbar({
  canSend,
  selectedModeId = DEFAULT_COMPOSER_MODE.id,
  onModeChange,
  onAttachFiles,
  onSend,
}: ComposerToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeMode = getComposerMode(selectedModeId);
  const ModeIcon = activeMode.Icon;

  return (
    <div
      data-testid="composer-toolbar"
      className="relative z-10 flex shrink-0 items-center justify-between border-t border-border/40 bg-muted/20 px-3"
      style={{ height: COMPOSER_TOOLBAR_HEIGHT }}
    >
      <div className="flex items-center gap-1.5">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) onAttachFiles?.(e.target.files);
            e.target.value = "";
          }}
        />
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Add attachment"
          onClick={() => fileInputRef.current?.click()}
          className="text-muted-foreground hover:text-foreground"
        >
          <Plus className="size-4" />
        </Button>

        <ModeMenu
          selectedId={selectedModeId}
          onSelect={(mode) => onModeChange?.(mode)}
          trigger={
            <Button
              variant="outline"
              size="sm"
              className="h-auto gap-1.5 border-border/60 bg-card px-2.5 py-1.5 text-[12px] text-foreground/70 hover:border-border hover:bg-muted/30"
            >
              <ModeIcon className="size-3.5 text-[var(--spark-orange)]" />
              {activeMode.buttonLabel}
              <ChevronDown className="size-3 text-muted-foreground/50" />
            </Button>
          }
        />
      </div>

      <Button
        variant={canSend ? "default" : "secondary"}
        size="sm"
        disabled={!canSend}
        onClick={onSend}
        className="h-auto gap-1.5 px-3 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ArrowUp className="size-3.5" />
        Send
      </Button>
    </div>
  );
}
